import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Package, ShoppingCart, Users, Plus, Settings, X, Eye, Truck } from 'lucide-react';
import { products } from '../mock';
import axios from 'axios';
import { toast } from 'sonner';

const Admin = () => {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  
  // Order detail modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Waybill modal state
  const [showWaybillModal, setShowWaybillModal] = useState(false);
  const [waybillOrder, setWaybillOrder] = useState(null);
  const [waybillNumber, setWaybillNumber] = useState('');
  const [addingWaybill, setAddingWaybill] = useState(false);
  
  // Backend URL
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Real orders data from backend
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Contact messages state
  const [contactMessages, setContactMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Dealer applications state
  const [dealerApplications, setDealerApplications] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(false);

  React.useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'contacts') {
      fetchContactMessages();
    } else if (activeTab === 'dealers') {
      fetchDealerApplications();
    }
  }, [activeTab]);
  
  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/orders`, {
        withCredentials: true
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchContactMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/contacts`, {
        withCredentials: true
      });
      setContactMessages(response.data);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const fetchDealerApplications = async () => {
    setLoadingDealers(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/dealer-applications`, {
        withCredentials: true
      });
      setDealerApplications(response.data);
    } catch (error) {
      console.error('Error fetching dealer applications:', error);
    } finally {
      setLoadingDealers(false);
    }
  };
  
  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };
  
  const openWaybillModal = (order) => {
    setWaybillOrder(order);
    setWaybillNumber('');
    setShowWaybillModal(true);
  };
  
  const addWaybillNumber = async () => {
    if (!waybillNumber.trim()) {
      toast.error('Please enter a waybill number');
      return;
    }
    
    setAddingWaybill(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/orders/${waybillOrder.id}/waybill`,
        { waybill_number: waybillNumber },
        { withCredentials: true }
      );
      
      // Update local state
      setOrders(orders.map(o => 
        o.id === waybillOrder.id ? { ...o, waybill_number: waybillNumber, shipment_status: 'SHIPPED' } : o
      ));
      
      toast.success('Waybill number added successfully!');
      setShowWaybillModal(false);
      setWaybillOrder(null);
      setWaybillNumber('');
    } catch (error) {
      console.error('Error adding waybill:', error);
      toast.error('Failed to add waybill number');
    } finally {
      setAddingWaybill(false);
    }
  };
  
  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/orders/${orderId}/status`,
        { orderStatus: newStatus },
        { withCredentials: true }
      );
      
      // Update local state
      setOrders(orders.map(o => 
        o.id === orderId ? { ...o, orderStatus: newStatus } : o
      ));
      
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, orderStatus: newStatus });
      }
      
      toast.success('Order status updated successfully!');
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!isAuthenticated || !isAdmin) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-rose-900 mb-2">Admin Dashboard</h1>
              <p className="text-gray-600">Manage your products and orders</p>
            </div>
            <div className="flex gap-3">
              <Link to="/admin/shipments">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Package className="h-4 w-4 mr-2" />
                  Shipments
                </Button>
              </Link>
              <Link to="/admin/settings">
                <Button className="bg-gray-600 hover:bg-gray-700 text-white">
                  <Settings className="h-4 w-4 mr-2" />
                  Website Settings
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Products</p>
                <p className="text-3xl font-bold text-rose-900 mt-2">{products.length}</p>
              </div>
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-rose-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Orders</p>
                <p className="text-3xl font-bold text-rose-900 mt-2">{orders.length}</p>
              </div>
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-rose-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Revenue</p>
                <p className="text-3xl font-bold text-rose-900 mt-2">₹{orders.reduce((sum, o) => sum + o.total, 0)}</p>
              </div>
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-rose-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('products')}
                className={`px-6 py-4 font-medium transition-colors duration-200 ${
                  activeTab === 'products'
                    ? 'border-b-2 border-rose-500 text-rose-600'
                    : 'text-gray-600 hover:text-rose-600'
                }`}
              >
                Products
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-4 font-medium transition-colors duration-200 ${
                  activeTab === 'orders'
                    ? 'border-b-2 border-rose-500 text-rose-600'
                    : 'text-gray-600 hover:text-rose-600'
                }`}
              >
                Orders
              </button>
              <button
                onClick={() => setActiveTab('contacts')}
                className={`px-6 py-4 font-medium transition-colors duration-200 ${
                  activeTab === 'contacts'
                    ? 'border-b-2 border-rose-500 text-rose-600'
                    : 'text-gray-600 hover:text-rose-600'
                }`}
              >
                Contact Messages
              </button>
              <button
                onClick={() => setActiveTab('dealers')}
                className={`px-6 py-4 font-medium transition-colors duration-200 ${
                  activeTab === 'dealers'
                    ? 'border-b-2 border-rose-500 text-rose-600'
                    : 'text-gray-600 hover:text-rose-600'
                }`}
              >
                Dealer Applications
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'products' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-rose-900">Products</h2>
                  <Button className="bg-rose-500 hover:bg-rose-600 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Product
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Product</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Category</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Price</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Stock</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.slice(0, 8).map(product => (
                        <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-3">
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                              <span className="font-medium">{product.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600 capitalize">{product.category}</td>
                          <td className="py-3 px-4 text-gray-600">₹{product.price}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-sm ${
                              product.inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {product.inStock ? 'In Stock' : 'Out of Stock'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50">
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'orders' && (
              <div>
                <h2 className="text-2xl font-bold text-rose-900 mb-6">Orders</h2>

                {loadingOrders ? (
                  <div className="text-center py-8">Loading orders...</div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No orders yet</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Order ID</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Customer</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Phone</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Pincode</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Items</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Total</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Payment</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Delivery</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map(order => (
                          <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">#{order.id.substring(0, 8)}</td>
                            <td className="py-3 px-4 text-gray-600">{order.shippingInfo?.fullName || 'N/A'}</td>
                            <td className="py-3 px-4 text-gray-600">{order.shippingInfo?.phone || 'N/A'}</td>
                            <td className="py-3 px-4 text-gray-600 font-mono text-sm">{order.shippingInfo?.pincode || 'N/A'}</td>
                            <td className="py-3 px-4 text-gray-600">{order.items?.length || 0} items</td>
                            <td className="py-3 px-4 text-gray-600 font-semibold">₹{order.totalAmount}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs ${
                                order.paymentMethod === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {order.paymentMethod === 'online' ? 'Online' : 'COD'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {order.waybill_number ? (
                                <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700 font-mono">
                                  ✓ {order.waybill_number}
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded text-xs bg-orange-100 text-orange-700">
                                  ⏳ Pending
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-sm ${
                                order.orderStatus === 'delivered' ? 'bg-green-100 text-green-700' :
                                order.orderStatus === 'processing' ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {order.orderStatus.charAt(0).toUpperCase() + order.orderStatus.slice(1)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-600">{new Date(order.createdAt).toLocaleDateString()}</td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <Button 
                                  onClick={() => viewOrderDetails(order)}
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-rose-600 hover:bg-rose-50"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                                {!order.waybill_number && (
                                  <Button 
                                    onClick={() => openWaybillModal(order)}
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-blue-600 hover:bg-blue-50"
                                  >
                                    <Truck className="h-4 w-4 mr-1" />
                                    Add Waybill
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'contacts' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Messages</h2>

                {loadingMessages ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading messages...</p>
                  </div>
                ) : contactMessages.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-lg">No contact messages yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contactMessages.map(message => (
                      <div key={message.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{message.name}</h3>
                            <p className="text-sm text-gray-600">{message.email}</p>
                            {message.phone && <p className="text-sm text-gray-600">{message.phone}</p>}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            message.status === 'new' ? 'bg-blue-100 text-blue-700' :
                            message.status === 'read' ? 'bg-gray-100 text-gray-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {message.status}
                          </span>
                        </div>
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">Subject:</p>
                          <p className="text-gray-900">{message.subject}</p>
                        </div>
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">Message:</p>
                          <p className="text-gray-600">{message.message}</p>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(message.createdAt).toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'dealers' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Dealer Applications</h2>

                {loadingDealers ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading applications...</p>
                  </div>
                ) : dealerApplications.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-lg">No dealer applications yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dealerApplications.map(app => (
                      <div key={app.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{app.name}</h3>
                            <p className="text-sm text-gray-600">{app.email} • {app.phone}</p>
                            <p className="text-sm text-gray-600">{app.city}, {app.state}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            app.status === 'approved' ? 'bg-green-100 text-green-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {app.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-xs font-medium text-gray-500">Business Experience</p>
                            <p className="text-sm text-gray-900">{app.businessExperience}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Warehouse Space</p>
                            <p className="text-sm text-gray-900">{app.warehouseSpace}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Investment Capacity</p>
                            <p className="text-sm text-gray-900">{app.investmentCapacity}</p>
                          </div>
                          {app.currentBusiness && (
                            <div>
                              <p className="text-xs font-medium text-gray-500">Current Business</p>
                              <p className="text-sm text-gray-900">{app.currentBusiness}</p>
                            </div>
                          )}
                        </div>
                        {app.additionalInfo && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">Additional Info:</p>
                            <p className="text-sm text-gray-600">{app.additionalInfo}</p>
                          </div>
                        )}
                        <div className="text-xs text-gray-500">
                          Applied: {new Date(app.createdAt).toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-rose-900">Order Details</h2>
                  <p className="text-gray-600 mt-1">Order ID: #{selectedOrder.id.substring(0, 8)}</p>
                </div>
                <button
                  onClick={() => setShowOrderDetail(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Customer & Shipping Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Customer Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Name:</span> {selectedOrder.shippingInfo?.fullName}</p>
                    <p><span className="font-medium">Email:</span> {selectedOrder.shippingInfo?.email || 'N/A'}</p>
                    <p><span className="font-medium">Phone:</span> {selectedOrder.shippingInfo?.phone}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Shipping Address</h3>
                  <div className="text-sm">
                    <p>{selectedOrder.shippingInfo?.address}</p>
                    <p>{selectedOrder.shippingInfo?.city}, {selectedOrder.shippingInfo?.state}</p>
                    <p>PIN: {selectedOrder.shippingInfo?.pincode}</p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Order Items</h3>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Product</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Weight</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Price</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Qty</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="py-3 px-4">{item.name}</td>
                          <td className="py-3 px-4 text-gray-600">{item.weight}</td>
                          <td className="py-3 px-4">₹{item.price}</td>
                          <td className="py-3 px-4">{item.quantity}</td>
                          <td className="py-3 px-4 text-right font-medium">₹{item.price * item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">₹{selectedOrder.totalAmount - (selectedOrder.shippingCharge || 100)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Shipping Charge:</span>
                    <span className="font-medium">₹{selectedOrder.shippingCharge || 100}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-rose-900 pt-2 border-t">
                    <span>Total Amount:</span>
                    <span>₹{selectedOrder.totalAmount}</span>
                  </div>
                </div>
              </div>

              {/* Payment & Status Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600 mb-1">Payment Method</p>
                  <p className="font-semibold">{selectedOrder.paymentMethod === 'online' ? 'Online Payment' : 'Cash on Delivery'}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600 mb-1">Payment Status</p>
                  <p className="font-semibold">{selectedOrder.paymentStatus === 'completed' ? 'Paid' : 'Pending'}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600 mb-1">Order Date</p>
                  <p className="font-semibold">{new Date(selectedOrder.createdAt).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Update Order Status */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Update Order Status</h3>
                <div className="flex gap-3">
                  <Button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'pending')}
                    disabled={updatingStatus || selectedOrder.orderStatus === 'pending'}
                    className={`${selectedOrder.orderStatus === 'pending' ? 'bg-yellow-500' : 'bg-gray-200 text-gray-700'} hover:bg-yellow-600`}
                  >
                    Pending
                  </Button>
                  <Button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'processing')}
                    disabled={updatingStatus || selectedOrder.orderStatus === 'processing'}
                    className={`${selectedOrder.orderStatus === 'processing' ? 'bg-blue-500' : 'bg-gray-200 text-gray-700'} hover:bg-blue-600`}
                  >
                    Processing
                  </Button>
                  <Button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'delivered')}
                    disabled={updatingStatus || selectedOrder.orderStatus === 'delivered'}
                    className={`${selectedOrder.orderStatus === 'delivered' ? 'bg-green-500' : 'bg-gray-200 text-gray-700'} hover:bg-green-600`}
                  >
                    Delivered
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Current status: <span className="font-semibold">{selectedOrder.orderStatus.charAt(0).toUpperCase() + selectedOrder.orderStatus.slice(1)}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Waybill Modal */}
      {showWaybillModal && waybillOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Add Waybill Number</h2>
              <button
                onClick={() => setShowWaybillModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Order: <span className="font-semibold">#{waybillOrder.id.substring(0, 8)}</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Customer: <span className="font-semibold">{waybillOrder.shippingInfo?.fullName}</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Pincode: <span className="font-semibold">{waybillOrder.shippingInfo?.pincode}</span>
              </p>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Waybill/Tracking Number *
                </label>
                <input
                  type="text"
                  value={waybillNumber}
                  onChange={(e) => setWaybillNumber(e.target.value)}
                  placeholder="Enter waybill number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowWaybillModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addWaybillNumber}
                disabled={addingWaybill || !waybillNumber.trim()}
                className="flex-1 px-4 py-2 bg-rose-700 text-white rounded-lg hover:bg-rose-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingWaybill ? 'Adding...' : 'Add Waybill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
